import { useEffect, useState, useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, formatErr } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { MARKET_CATEGORIES, ITEM_CONDITIONS, fmtPrice, conditionLabel } from "@/lib/market";
import { Search, MapPin, Tag, Plus, Flame, BadgeCheck, Filter, ShieldCheck } from "lucide-react";

export default function Market() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [items, setItems] = useState(null);
  const [q, setQ] = useState("");
  const [category, setCategory] = useState("");
  const [location, setLocation] = useState("");
  const [condition, setCondition] = useState("");
  const [minPrice, setMinPrice] = useState("");
  const [maxPrice, setMaxPrice] = useState("");
  const [err, setErr] = useState("");

  const load = async () => {
    setErr("");
    const p = new URLSearchParams();
    if (q.trim()) p.set("q", q.trim());
    if (category) p.set("category", category);
    if (location.trim()) p.set("location", location.trim());
    if (condition) p.set("condition", condition);
    if (minPrice) p.set("min_price", minPrice);
    if (maxPrice) p.set("max_price", maxPrice);
    try { const { data } = await api.get(`/marketplace?${p}`); setItems(data || []); }
    catch (e) { setErr(formatErr(e)); }
  };

  useEffect(() => { load(); }, []);
  useEffect(() => { const t = setTimeout(load, 300); return () => clearTimeout(t); }, [q]);

  const boosted = useMemo(() => (items || []).filter(i => i.is_boosted), [items]);
  const organic = useMemo(() => (items || []).filter(i => !i.is_boosted), [items]);

  return (
    <div className="max-w-6xl mx-auto px-6 sm:px-8 py-10" data-testid="market-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="overline mb-1">For-Sale Marketplace</p>
          <h1 className="h-display text-4xl sm:text-5xl">Buy & sell, human-to-human.</h1>
          <p className="text-[#4A4A4A] mt-2 max-w-xl">
            Real estate, cars, electronics, furniture — anything you have, sell it to a real human. Pay safely on Hoomanz, or chat directly.
          </p>
        </div>
        <div className="flex gap-2">
          <Link to="/market/mine" className="btn-ghost" data-testid="market-mine-cta">My listings</Link>
          <Link to="/market/new" className="btn-primary" data-testid="market-post-cta"><Plus size={14}/> List an item</Link>
        </div>
      </div>

      <div className="card p-4 mt-7" data-testid="market-filters">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-2">
          <div className="md:col-span-4 relative">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#8A8A8A]"/>
            <input className="input pl-9" placeholder="Search items…" value={q}
              onChange={(e)=>setQ(e.target.value)} data-testid="market-search"/>
          </div>
          <div className="md:col-span-3 relative">
            <MapPin size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#8A8A8A]"/>
            <input className="input pl-9" placeholder="Location" value={location}
              onChange={(e)=>setLocation(e.target.value)} onBlur={load} data-testid="market-location"/>
          </div>
          <select className="input md:col-span-2" value={category}
            onChange={(e)=>{ setCategory(e.target.value); setTimeout(load, 0); }} data-testid="market-category">
            <option value="">All categories</option>
            {MARKET_CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
          </select>
          <select className="input md:col-span-2" value={condition}
            onChange={(e)=>{ setCondition(e.target.value); setTimeout(load, 0); }} data-testid="market-condition">
            <option value="">Any condition</option>
            {ITEM_CONDITIONS.map(c => <option key={c.id} value={c.id}>{c.label}</option>)}
          </select>
          <div className="md:col-span-1 flex gap-1">
            <input className="input w-1/2" placeholder="$min" type="number" min="0"
              value={minPrice} onChange={(e)=>setMinPrice(e.target.value)} onBlur={load} data-testid="market-min-price"/>
            <input className="input w-1/2" placeholder="$max" type="number" min="0"
              value={maxPrice} onChange={(e)=>setMaxPrice(e.target.value)} onBlur={load} data-testid="market-max-price"/>
          </div>
        </div>
      </div>

      {err && <p className="text-sm text-[#B84B4B] mt-4">{err}</p>}
      {items === null && <p className="text-[#8A8A8A] mt-10">Loading…</p>}
      {items && items.length === 0 && (
        <div className="text-center py-20" data-testid="market-empty">
          <Tag size={28} className="mx-auto text-[#0057FF] mb-3"/>
          <p className="text-[#4A4A4A]">Nothing for sale here yet — be the first to list.</p>
          {user && <Link to="/market/new" className="btn-secondary mt-4 inline-flex"><Plus size={14}/> List an item</Link>}
        </div>
      )}

      {boosted.length > 0 && (
        <section className="mt-8">
          <p className="overline mb-3 text-[#0057FF] inline-flex items-center gap-1.5"><Flame size={12}/> Boosted</p>
          <Grid items={boosted} onClick={(id)=>nav(`/market/${id}`)} boosted/>
        </section>
      )}
      {organic.length > 0 && (
        <section className="mt-8">
          {boosted.length > 0 && <p className="overline mb-3 inline-flex items-center gap-1.5"><Filter size={12}/> All listings</p>}
          <Grid items={organic} onClick={(id)=>nav(`/market/${id}`)}/>
        </section>
      )}
    </div>
  );
}

function Grid({ items, onClick, boosted }) {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid={boosted ? "market-boosted-grid" : "market-list"}>
      {items.map(i => (
        <button key={i.id} onClick={()=>onClick(i.id)} data-testid={`market-tile-${i.id}`}
          className={`card overflow-hidden text-left hover:border-[#0A0A0A] transition-colors ${boosted ? "ring-1 ring-[#0057FF]/40" : ""}`}>
          <div className="aspect-[4/3] bg-[#F2F2F0] overflow-hidden">
            {i.images?.[0] ? (
              <img src={i.images[0]} alt={i.title} className="w-full h-full object-cover"/>
            ) : (
              <div className="w-full h-full flex items-center justify-center text-[#8A8A8A]">
                <Tag size={24}/>
              </div>
            )}
          </div>
          <div className="p-4">
            <div className="flex items-center gap-1.5 flex-wrap">
              <h3 className="font-medium leading-tight truncate flex-1">{i.title}</h3>
              {i.is_boosted && (
                <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded-full bg-[#0057FF] text-white">
                  {i.boost_tier}
                </span>
              )}
            </div>
            <p className="text-sm text-[#4A4A4A] mt-0.5">{i.category} · {conditionLabel(i.condition)}</p>
            <div className="flex items-center justify-between mt-2">
              <p className="font-medium text-lg">{fmtPrice(i)}</p>
              <span className="text-xs text-[#8A8A8A] inline-flex items-center gap-1">
                <MapPin size={11}/> {i.location || "—"}
              </span>
            </div>
            <p className="text-xs text-[#8A8A8A] mt-2 inline-flex items-center gap-1">
              by <span className="font-medium text-[#0A0A0A]">{i.seller?.name}</span>
              {i.seller?.verified && <BadgeCheck size={11} className="text-[#0A0A0A]"/>}
              <span className="mx-1 opacity-50">·</span>
              <ShieldCheck size={11}/> Paid on Hoomanz
            </p>
          </div>
        </button>
      ))}
    </div>
  );
}
